import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import './Level1.css';
import { unlockNextLevel } from './Levelselect';

import Preloader from '../components/layout/Preloader';
import Header from '../components/layout/Header';
import PausePopup from '../components/layout/Pausepopup';
import WinPopup from '../components/layout/Winpopup';
import LosePopup from '../components/layout/Losepopup';
import level1Bg from '../asserts/level1bg.png';
import leftChar from '../asserts/avatar.png';
import rightChar from '../asserts/av4.png';
import fragmentImg from '../asserts/fragment4.png';

import puzzle1 from '../asserts/puzzle.png';
import puzzle2 from '../asserts/puzzle2.jpg';
import puzzle3 from '../asserts/puzzle3.jpg';

const PUZZLE_IMAGES = [puzzle1, puzzle2, puzzle3];

const COLS = 3;
const ROWS = 3;
const TOTAL = COLS * ROWS;

const CELL_W = 160;
const CELL_H = 160;
const TAB    = 22;
const PW     = CELL_W + TAB * 2;
const PH     = CELL_H + TAB * 2;

const TIMER_SECONDS = 30;
const SHUFFLE_AT    = 15;

const DIALOGUES = [
  { side: 'left',  text: '"Scribe, give me the fourth heart fragment — or I will take it."' },
  { side: 'right', text: '"I cannot read these holographic shards, Shadow. Rebuild the papyrus for me and the heart piece is yours; fail, and you leave empty-handed."' },
];

const TASK2_POOL = [
  {
    question: '"The fourth seal cannot be broken without knowledge. What curse did the dark priests carve into the tomb walls?"',
    hint: 'Hint: It was meant to stop anyone who disturbed the King\'s rest.',
    answers: [
      { text: 'A blessing to guide the pharaoh into the afterlife.', correct: false },
      { text: 'A death curse upon all who dared to wake the sleeping King.', correct: true },
      { text: 'A prayer to summon the gods of war.', correct: false },
    ],
  },
  {
    question: '"The final guardian of the tomb carried a weapon forged in darkness. What power did it hold?"',
    hint: 'Hint: It could sever the bond between body and shadow.',
    answers: [
      { text: 'It could turn stone into gold at a single touch.', correct: false },
      { text: 'It could split a soul from its mortal form forever.', correct: true },
      { text: 'It could open any sealed gate in the underworld.', correct: false },
    ],
  },
  {
    question: '"The darkest chapter of the papyrus speaks of a ritual performed only once every thousand years. What was its purpose?"',
    hint: 'Hint: It required the heart of a king and the silence of the gods.',
    answers: [
      { text: 'To crown a new pharaoh under the blood moon.', correct: false },
      { text: 'To restore a fallen king to life by sealing his heart fragments.', correct: true },
      { text: 'To open the gates of the underworld and release the dead.', correct: false },
    ],
  },
];

const PIECE_EDGES = [
  [  0,  1,  1,  0 ],
  [  0,  1,  1, -1 ],
  [  0,  0,  1, -1 ],
  [ -1,  1,  1,  0 ],
  [ -1,  1,  1, -1 ],
  [ -1,  0,  1, -1 ],
  [ -1,  1,  0,  0 ],
  [ -1,  1,  0, -1 ],
  [ -1,  0,  0, -1 ],
];

function buildJigsawPath(edges) {
  const [top, right, bottom, left] = edges;
  const x0 = TAB, y0 = TAB;
  const x1 = TAB + CELL_W, y1 = TAB + CELL_H;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  const tr = TAB * 0.88;
  let d = `M ${x0} ${y0}`;
  if (top === 0) { d += ` L ${x1} ${y0}`; } else {
    const dir = -top;
    d += ` L ${mx - tr} ${y0} C ${mx - tr} ${y0 + dir * tr * 0.5}, ${mx - tr * 0.5} ${y0 + dir * tr}, ${mx} ${y0 + dir * tr} C ${mx + tr * 0.5} ${y0 + dir * tr}, ${mx + tr} ${y0 + dir * tr * 0.5}, ${mx + tr} ${y0} L ${x1} ${y0}`;
  }
  if (right === 0) { d += ` L ${x1} ${y1}`; } else {
    const dir = right;
    d += ` L ${x1} ${my - tr} C ${x1 + dir * tr * 0.5} ${my - tr}, ${x1 + dir * tr} ${my - tr * 0.5}, ${x1 + dir * tr} ${my} C ${x1 + dir * tr} ${my + tr * 0.5}, ${x1 + dir * tr * 0.5} ${my + tr}, ${x1} ${my + tr} L ${x1} ${y1}`;
  }
  if (bottom === 0) { d += ` L ${x0} ${y1}`; } else {
    const dir = bottom;
    d += ` L ${mx + tr} ${y1} C ${mx + tr} ${y1 + dir * tr * 0.5}, ${mx + tr * 0.5} ${y1 + dir * tr}, ${mx} ${y1 + dir * tr} C ${mx - tr * 0.5} ${y1 + dir * tr}, ${mx - tr} ${y1 + dir * tr * 0.5}, ${mx - tr} ${y1} L ${x0} ${y1}`;
  }
  if (left === 0) { d += ` L ${x0} ${y0}`; } else {
    const dir = -left;
    d += ` L ${x0} ${my + tr} C ${x0 + dir * tr * 0.5} ${my + tr}, ${x0 + dir * tr} ${my + tr * 0.5}, ${x0 + dir * tr} ${my} C ${x0 + dir * tr} ${my - tr * 0.5}, ${x0 + dir * tr * 0.5} ${my - tr}, ${x0} ${my - tr} L ${x0} ${y0}`;
  }
  d += ' Z';
  return d;
}

const PIECE_PATHS = PIECE_EDGES.map(buildJigsawPath);
const makePieces = () => Array.from({ length: TOTAL }, (_, i) => ({ id: i, correctSlot: i }));
const shuffleArr = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function JigsawPiece({ correctSlot, puzzleImg }) {
  const col    = correctSlot % COLS;
  const row    = Math.floor(correctSlot / COLS);
  const path   = PIECE_PATHS[correctSlot];
  const clipId = `clip-piece-${correctSlot}`;
  const imgW   = COLS * CELL_W;
  const imgH   = ROWS * CELL_H;
  const imgX   = TAB - col * CELL_W;
  const imgY   = TAB - row * CELL_H;
  return (
    <svg width={PW} height={PH} viewBox={`0 0 ${PW} ${PH}`} style={{ display: 'block', overflow: 'visible', cursor: 'grab' }}>
      <defs>
        <clipPath id={clipId}><path d={path} /></clipPath>
        <filter id={`pshadow-${correctSlot}`} x="-20%" y="-20%" width="150%" height="150%">
          <feDropShadow dx="3" dy="5" stdDeviation="5" floodColor="rgba(0,0,0,0.65)" />
        </filter>
      </defs>
      <path d={path} fill="url(#woodGrad)" stroke="#7a5218" strokeWidth="1.5" filter={`url(#pshadow-${correctSlot})`} />
      <image href={puzzleImg} x={imgX} y={imgY} width={imgW} height={imgH} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
      <path d={path} fill="rgba(160,100,30,0.07)" stroke="#c8a050" strokeWidth="1" clipPath={`url(#${clipId})`} />
    </svg>
  );
}

function BoardSlot({ slotIndex, piece, onDragOver, onDrop, onPieceDragStart, puzzleImg }) {
  const path      = PIECE_PATHS[slotIndex];
  const isCorrect = piece && piece.correctSlot === slotIndex;
  const isWrong   = piece && piece.correctSlot !== slotIndex;
  const strokeColor = isCorrect ? 'rgba(80,220,100,0.9)' : isWrong ? 'rgba(220,60,60,0.9)' : 'rgba(180,140,60,0.3)';
  const fillColor   = isCorrect ? 'rgba(40,160,60,0.08)' : isWrong ? 'rgba(180,40,40,0.08)' : 'rgba(15,8,0,0.45)';
  const glowColor   = isCorrect ? 'rgba(80,220,100,0.7)' : isWrong ? 'rgba(220,60,60,0.7)' : 'transparent';
  return (
    <div style={{ width: PW, height: PH, position: 'relative' }} onDragOver={onDragOver} onDrop={onDrop}>
      <svg width={PW} height={PH} viewBox={`0 0 ${PW} ${PH}`} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}>
        <defs>
          <filter id={`glow-s${slotIndex}`}>
            <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor={glowColor} />
          </filter>
        </defs>
        <path d={path} fill={fillColor} stroke={strokeColor} strokeWidth={(isCorrect || isWrong) ? 2.5 : 1.5}
          strokeDasharray={(isCorrect || isWrong) ? 'none' : '6 4'}
          filter={(isCorrect || isWrong) ? `url(#glow-s${slotIndex})` : undefined} />
      </svg>
      {piece && (
        <div style={{ position: 'absolute', top: 0, left: 0 }} draggable onDragStart={onPieceDragStart}>
          <JigsawPiece correctSlot={piece.correctSlot} puzzleImg={puzzleImg} />
        </div>
      )}
    </div>
  );
}

function Task2({ onCorrect, onWrong, task2 }) {
  const [showWin, setShowWin] = useState(false);
  const answeredRef   = useRef(false);
  const completedRef  = useRef(false);
  const winMsgRef     = useRef();
  const winFragRef    = useRef();
  const winOverlayRef = useRef();

  const handleAnswer = (correct) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    if (correct) setShowWin(true);
    else onWrong();
  };

  useEffect(() => {
    if (!showWin || completedRef.current) return;
    completedRef.current = true;
    const tl = gsap.timeline({ onComplete: () => setTimeout(() => onCorrect(), 1200) });
    if (winOverlayRef.current) tl.fromTo(winOverlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' });
    if (winMsgRef.current) tl.fromTo(winMsgRef.current, { opacity: 0, y: -60, scale: 0.75 }, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2)' }, '-=0.1');
    if (winFragRef.current) {
      tl.fromTo(winFragRef.current, { opacity: 0, scale: 0, rotation: -25, y: 80 }, { opacity: 1, scale: 1, rotation: 0, y: 0, duration: 0.85, ease: 'elastic.out(1,0.55)' }, '-=0.2');
      tl.to(winFragRef.current, { y: -18, duration: 1.1, ease: 'sine.inOut', yoyo: true, repeat: 3 }, '+=0.1');
    }
  }, [showWin, onCorrect]);

  return (
    <div className="task2-overlay">
      {!showWin ? (
        <>
          <div className="task2-question-box">
            <p className="task2-question-text">{task2.question}</p>
            <p className="task2-hint-text">{task2.hint}</p>
          </div>
          <div className="task2-answers">
            {task2.answers.map((ans, i) => (
              <button key={i} className="task2-answer-btn" onClick={() => handleAnswer(ans.correct)}>{ans.text}</button>
            ))}
          </div>
        </>
      ) : (
        <div className="task2-win-overlay" ref={winOverlayRef} style={{ opacity: 0 }}>
          <div className="task2-win-msg" ref={winMsgRef} style={{ opacity: 0 }}>
            <p>"You have got it! The fourth fragment is yours."</p>
          </div>
          <div className="task2-win-fragment" ref={winFragRef} style={{ opacity: 0 }}>
            <img src={fragmentImg} alt="Heart Fragment" />
          </div>
        </div>
      )}
    </div>
  );
}

const Level4 = () => {
  const navigate = useNavigate();

  const [puzzleImg] = useState(() => PUZZLE_IMAGES[Math.floor(Math.random() * PUZZLE_IMAGES.length)]);
  const [task2] = useState(() => TASK2_POOL[Math.floor(Math.random() * TASK2_POOL.length)]);

  const [showPreloader, setShowPreloader] = useState(true);
  const [introsDone,    setIntrosDone]    = useState(false);
  const [gameStarted,   setGameStarted]   = useState(false);
  const [timeLeft,      setTimeLeft]      = useState(TIMER_SECONDS);
  const [shuffled,      setShuffled]      = useState(false);
  const [won,           setWon]           = useState(false);
  const [showTask2,     setShowTask2]      = useState(false);
  const [showPause,     setShowPause]      = useState(false);
  const [showWinPopup,  setShowWinPopup]   = useState(false);
  const [showLosePopup, setShowLosePopup]  = useState(false);

  const [traySlots, setTraySlots] = useState(() => {
    const sh = shuffleArr(makePieces());
    return { left: [sh[0],sh[1],sh[2],sh[3],sh[4]], right: [sh[5],sh[6],sh[7],sh[8]] };
  });
  const [slots,     setSlots]     = useState(Array(TOTAL).fill(null));
  const [dragPiece, setDragPiece] = useState(null);
  const [dragFrom,  setDragFrom]  = useState(null);

  const hintRef        = useRef();
  const timerRef       = useRef();
  const intervalRef    = useRef();
  const headerRef      = useRef();
  const leftTrayRef    = useRef();
  const rightTrayRef   = useRef();
  const boardRef       = useRef();
  const layoutRef      = useRef();
  const task2Ref       = useRef();
  const hintBounce     = useRef(null);
  const pauseHandler   = useRef(null);
  const isShufflingRef = useRef(false);

  const returnPieceToTray = useCallback((piece) => {
    setTraySlots(prev => {
      const li = prev.left.findIndex(p => p === null);
      if (li !== -1) { const l = [...prev.left]; l[li] = piece; return { ...prev, left: l }; }
      const ri = prev.right.findIndex(p => p === null);
      if (ri !== -1) { const r = [...prev.right]; r[ri] = piece; return { ...prev, right: r }; }
      return prev;
    });
  }, []);

  const animatedShuffleTray = useCallback(() => {
    if (isShufflingRef.current) return;
    isShufflingRef.current = true;
    const leftEl  = leftTrayRef.current;
    const rightEl = rightTrayRef.current;
    if (!leftEl || !rightEl) { isShufflingRef.current = false; return; }
    const tl = gsap.timeline({ onComplete: () => { isShufflingRef.current = false; } });
    tl.to(leftEl,  { x: 300, rotation: 15, opacity: 0, scale: 0.8, duration: 0.45, ease: 'power3.in' })
      .to(rightEl, { x: -300, rotation: -15, opacity: 0, scale: 0.8, duration: 0.45, ease: 'power3.in' }, '<')
      .call(() => {
        setTraySlots(prev => {
          const allPieces = [...prev.left, ...prev.right].filter(Boolean);
          const sh = shuffleArr(allPieces);
          const half = Math.ceil(sh.length / 2);
          return {
            left:  prev.left.map((_, i) => sh[i] || null),
            right: prev.right.map((_, i) => sh[half + i] || null),
          };
        });
      })
      .set(leftEl,  { x: -280, rotation: -12, scale: 0.8, opacity: 0 })
      .set(rightEl, { x:  280, rotation:  12, scale: 0.8, opacity: 0 })
      .to(leftEl,  { x: 0, rotation: 0, opacity: 1, scale: 1, duration: 0.55, ease: 'back.out(1.8)' })
      .to(rightEl, { x: 0, rotation: 0, opacity: 1, scale: 1, duration: 0.55, ease: 'back.out(1.8)' }, '-=0.35');
  }, []);

  const runIntroAnimation = useCallback(() => {
    const tl = gsap.timeline();
    tl.fromTo(headerRef.current,   { opacity: 0, y: -60 },        { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' })
      .fromTo(leftTrayRef.current,  { opacity: 0, x: -140, scale: 0.8 }, { opacity: 1, x: 0, scale: 1, duration: 0.85, ease: 'back.out(1.5)' }, '-=0.2')
      .fromTo(rightTrayRef.current, { opacity: 0, x:  140, scale: 0.8 }, { opacity: 1, x: 0, scale: 1, duration: 0.85, ease: 'back.out(1.5)' }, '<')
      .fromTo(boardRef.current,     { opacity: 0, scale: 0.7, rotateY: 14 }, { opacity: 1, scale: 1, rotateY: 0, duration: 1, ease: 'power4.out' }, '-=0.45')
      .fromTo(hintRef.current,      { opacity: 0, y: -50, scale: 0.82 }, {
        opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2.2)',
        onComplete: () => {
          hintBounce.current = gsap.to(hintRef.current, { y: -10, duration: 0.65, ease: 'sine.inOut', yoyo: true, repeat: -1 });
        }
      }, '-=0.2');
  }, []);

  useEffect(() => { if (introsDone) runIntroAnimation(); }, [introsDone, runIntroAnimation]);

  const startGame = useCallback(() => {
    if (gameStarted) return;
    setGameStarted(true);
    if (hintBounce.current) { hintBounce.current.kill(); hintBounce.current = null; }
    gsap.to(hintRef.current, {
      opacity: 0, y: -30, scale: 0.85, duration: 0.4, ease: 'power2.in',
      onComplete: () => {
        if (hintRef.current) hintRef.current.style.visibility = 'hidden';
        gsap.fromTo(timerRef.current, { opacity: 0, y: -40, scale: 0.78 }, { opacity: 1, y: 0, scale: 1, duration: 0.65, ease: 'back.out(2)' });
      },
    });
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(intervalRef.current); setShowLosePopup(true); return 0; }
        if (t - 1 === SHUFFLE_AT) {
          setShuffled(prev => { if (!prev) { setTimeout(animatedShuffleTray, 0); return true; } return prev; });
        }
        return t - 1;
      });
    }, 1000);
  }, [gameStarted, animatedShuffleTray]);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  pauseHandler.current = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowPause(true);
  };

  const handleResume = () => {
    setShowPause(false);
    if (!gameStarted) return;
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(intervalRef.current); setShowLosePopup(true); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (!won) return;
    clearInterval(intervalRef.current);
    const tl = gsap.timeline({ onComplete: () => setShowTask2(true) });
    tl.to(leftTrayRef.current,  { x: -220, opacity: 0, rotation: -15, duration: 0.6, ease: 'power3.in' })
      .to(rightTrayRef.current, { x:  220, opacity: 0, rotation:  15, duration: 0.6, ease: 'power3.in' }, '<')
      .to(boardRef.current,     { scaleY: 0, opacity: 0, duration: 0.5, ease: 'power4.in' }, '-=0.3')
      .to(timerRef.current,     { opacity: 0, y: -30, duration: 0.3, ease: 'power2.in' }, '<');
  }, [won]);

  useEffect(() => {
    if (!showTask2 || !task2Ref.current) return;
    const tl = gsap.timeline();
    tl.fromTo(task2Ref.current, { opacity: 0, y: '100vh', scale: 0.85 }, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'expo.out' });
    const box  = task2Ref.current.querySelector('.task2-question-box');
    const btns = task2Ref.current.querySelectorAll('.task2-answer-btn');
    if (box)         tl.fromTo(box,  { opacity: 0, y: 40, rotateX: -20 }, { opacity: 1, y: 0, rotateX: 0, duration: 0.6, ease: 'back.out(1.8)' }, '-=0.4');
    if (btns.length) tl.fromTo(btns, { opacity: 0, y: 50, scale: 0.8  }, { opacity: 1, y: 0, scale: 1,  duration: 0.5, ease: 'back.out(2)', stagger: 0.12 }, '-=0.3');
  }, [showTask2]);

  const handleDragStart = (piece, fromType, fromIdx) => {
    setDragPiece(piece);
    setDragFrom({ type: fromType, idx: fromIdx });
    if (!gameStarted) startGame();
  };

  const handleDropOnSlot = (slotIndex) => {
    if (!dragPiece) return;
    const newSlots = [...slots];
    if (dragFrom.type === 'board') {
      const prev = newSlots.findIndex(p => p && p.id === dragPiece.id);
      if (prev !== -1) newSlots[prev] = null;
    }
    const displaced = newSlots[slotIndex];
    if (displaced !== null) {
      if (dragFrom.type === 'tray') returnPieceToTray(displaced);
      else {
        const oldIdx = slots.findIndex(p => p && p.id === dragPiece.id);
        if (oldIdx !== -1) newSlots[oldIdx] = displaced;
        else returnPieceToTray(displaced);
      }
    }
    newSlots[slotIndex] = dragPiece;
    if (dragFrom.type === 'tray') {
      const leftLen = traySlots.left.length;
      const isLeft  = dragFrom.idx < leftLen;
      const side    = isLeft ? 'left' : 'right';
      const i       = isLeft ? dragFrom.idx : dragFrom.idx - leftLen;
      setTraySlots(prev => { const arr = [...prev[side]]; arr[i] = null; return { ...prev, [side]: arr }; });
    }
    setSlots(newSlots);
    setDragPiece(null);
    setDragFrom(null);
    if (newSlots.every((p, i) => p && p.correctSlot === i)) setWon(true);
  };

  const handleDropOnTray = () => {
    if (!dragPiece || dragFrom.type !== 'board') return;
    const newSlots = [...slots];
    const prev = newSlots.findIndex(p => p && p.id === dragPiece.id);
    if (prev !== -1) newSlots[prev] = null;
    setSlots(newSlots);
    returnPieceToTray(dragPiece);
    setDragPiece(null);
    setDragFrom(null);
  };

  const mins = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const secs = String(timeLeft % 60).padStart(2, '0');

  if (showPreloader) return <Preloader onComplete={() => setShowPreloader(false)} />;

  return (
    <div className="level1-page" style={{ backgroundImage: `url(${level1Bg})` }}>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="woodGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#ecd08e" />
            <stop offset="45%"  stopColor="#d4a96a" />
            <stop offset="100%" stopColor="#b07a3a" />
          </linearGradient>
        </defs>
      </svg>

      {!introsDone && (() => {
        const LevelIntro = require('../components/layout/Levelintro').default;
        return <LevelIntro leftImage={leftChar} rightImage={rightChar} dialogues={DIALOGUES} onFinish={() => setIntrosDone(true)} />;
      })()}

      <div ref={headerRef} style={{ opacity: 0, position: 'relative', zIndex: 100 }}>
        <Header mode="game" onPause={() => { if (pauseHandler.current) pauseHandler.current(); }} />
      </div>

      <div className="hint-box" ref={hintRef} style={{ opacity: 0 }}>
        <p>Solve the puzzle within time</p>
      </div>

      <div className="timer-box" ref={timerRef} style={{ opacity: 0 }}>
        <span className={timeLeft <= 10 ? 'timer-urgent' : ''}>{mins}:{secs}</span>
      </div>

      {!showTask2 && (
        <div className="puzzle-layout" ref={layoutRef}>
          <div className="tray tray-left" ref={leftTrayRef} style={{ opacity: 0 }}
            onDragOver={e => e.preventDefault()} onDrop={handleDropOnTray}>
            {traySlots.left.map((piece, idx) => (
              <div key={idx} className="tray-slot" style={{ width: PW, height: PH }}>
                {piece
                  ? <div draggable onDragStart={() => handleDragStart(piece, 'tray', idx)} style={{ lineHeight: 0 }}><JigsawPiece correctSlot={piece.correctSlot} puzzleImg={puzzleImg} /></div>
                  : <div style={{ width: PW, height: PH }} />}
              </div>
            ))}
          </div>

          <div className="puzzle-board" ref={boardRef} style={{ opacity: 0, width: COLS*CELL_W+(COLS-1)*4+10, height: ROWS*CELL_H+(ROWS-1)*4+10 }}>
            <div className="puzzle-image-bg" style={{ backgroundImage: `url(${puzzleImg})` }} />
            <div className="board-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS}, ${CELL_W}px)`, gridTemplateRows: `repeat(${ROWS}, ${CELL_H}px)`, gap: '4px', position: 'relative', zIndex: 1 }}>
              {slots.map((piece, i) => (
                <div key={i} style={{ position: 'relative', width: CELL_W, height: CELL_H, overflow: 'visible' }}>
                  <div style={{ position: 'absolute', top: -TAB, left: -TAB, zIndex: piece ? 2 : 1 }}>
                    <BoardSlot slotIndex={i} piece={piece} puzzleImg={puzzleImg}
                      onDragOver={e => e.preventDefault()}
                      onDrop={() => handleDropOnSlot(i)}
                      onPieceDragStart={() => handleDragStart(piece, 'board', i)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tray tray-right" ref={rightTrayRef} style={{ opacity: 0 }}
            onDragOver={e => e.preventDefault()} onDrop={handleDropOnTray}>
            {traySlots.right.map((piece, idx) => (
              <div key={idx} className="tray-slot" style={{ width: PW, height: PH }}>
                {piece
                  ? <div draggable onDragStart={() => handleDragStart(piece, 'tray', idx + traySlots.left.length)} style={{ lineHeight: 0 }}><JigsawPiece correctSlot={piece.correctSlot} puzzleImg={puzzleImg} /></div>
                  : <div style={{ width: PW, height: PH }} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {showTask2 && (
        <div ref={task2Ref} style={{ opacity: 0 }}>
          <Task2
            task2={task2}
            onCorrect={() => { unlockNextLevel(4); if (!showWinPopup) setShowWinPopup(true); }}
            onWrong={() => { if (!showLosePopup) setShowLosePopup(true); }}
          />
        </div>
      )}

      {showPause && <PausePopup onResume={handleResume} onLevels={() => navigate('/levels')} onMenu={() => navigate('/mainmenu')} />}
      {showWinPopup && <WinPopup currentLevelId={4} fragmentImg={fragmentImg} onNext={() => navigate('/level5')} />}
      {showLosePopup && <LosePopup onReplay={() => navigate(0)} />}
    </div>
  );
};

export default Level4;